/**
 * Pillager outpost (vanilla pillager_outpost/watchtower): a cobblestone plinth
 * under a three-storey tower of birch planks framed in dark oak logs, dark oak
 * floors, and an overhanging lookout deck under a peaked dark oak roof, with
 * the loot chest on the deck.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';
import { hasVillageStyle } from './village';

const COBBLE = block('Cobblestone');
const LOG = block('Dark Oak Log');
const FLOOR = block('Dark Oak Planks');
const WALL = block('Birch Planks');
const TORCH = block('Torch');

function buildOutpost(b: Builder): void {
  const y = b.y;
  b.fill(-2, y + 1, -2, 8, y + 24, 8, AIR);
  b.foundations(0, 0, 6, 6, y, COBBLE, 8);
  b.fill(0, y, 0, 6, y, 6, COBBLE);
  b.walls(0, y + 1, 0, 6, y + 2, 6, COBBLE);
  // three storeys of birch, dark oak at the corners and on every floor
  b.walls(0, y + 3, 0, 6, y + 15, 6, WALL);
  for (const [u, v] of [[0, 0], [6, 0], [0, 6], [6, 6]]) b.fill(u, y + 1, v, u, y + 16, v, LOG);
  for (const fy of [y + 6, y + 11]) { b.fill(1, fy, 1, 5, fy, 5, FLOOR); b.set(1, fy, 1, AIR); } // with a stairwell hole
  for (const fy of [y + 6, y + 11]) b.walls(0, fy, 0, 6, fy, 6, LOG);
  // entrance and windows
  b.fill(3, y + 1, 0, 3, y + 2, 0, AIR);
  for (const wy of [y + 8, y + 13]) for (const [u, v] of [[3, 0], [3, 6], [0, 3], [6, 3]]) b.set(u, wy, v, AIR);
  // the lookout: a deck a block wider all round, log railing, peaked roof on posts
  b.fill(-1, y + 16, -1, 7, y + 16, 7, FLOOR);
  b.walls(-1, y + 17, -1, 7, y + 17, 7, LOG);
  b.fill(3, y + 17, 3, 3, y + 17, 3, AIR);
  for (const [u, v] of [[-1, -1], [7, -1], [-1, 7], [7, 7]]) b.fill(u, y + 18, v, u, y + 19, v, LOG);
  for (let i = 0; i <= 4; i++) b.fill(-1 + i, y + 20 + i, -1 + i, 7 - i, y + 20 + i, 7 - i, FLOOR);
  b.chest(3, y + 17, 5, F.NV);
  b.set(1, y + 17, 1, TORCH);
  b.set(5, y + 7, 5, TORCH);
}

function outpost(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  const ok = hasVillageStyle(c.biome) || c.biome === Biome.Grove || c.biome === Biome.CherryGrove
    || c.biome === Biome.SnowySlopes;
  if (!ok || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, int(rnd, 4), 7, 7, c.height, true, seedOf(rnd), buildOutpost)];
}

export const PILLAGER_OUTPOST: StructureType = {
  name: 'Pillager Outpost', spacing: 32, separation: 8, salt: 165745296, reach: 1, generate: outpost,
};
